// Google Analytics 4 連接診斷工具

export interface GA4DiagnosticResult {
  isConfigured: boolean
  measurementId: string | null
  isScriptLoaded: boolean
  isGtagAvailable: boolean
  isDataSending: boolean
  errors: string[]
  warnings: string[]
  recommendations: string[]
}

export class GA4Debugger {
  private static instance: GA4Debugger
  
  static getInstance(): GA4Debugger {
    if (!GA4Debugger.instance) {
      GA4Debugger.instance = new GA4Debugger()
    }
    return GA4Debugger.instance
  }

  /**
   * 全面診斷 GA4 連接狀態
   */
  async diagnose(): Promise<GA4DiagnosticResult> {
    const result: GA4DiagnosticResult = {
      isConfigured: false,
      measurementId: null,
      isScriptLoaded: false,
      isGtagAvailable: false,
      isDataSending: false,
      errors: [],
      warnings: [],
      recommendations: []
    }

    // 使用固定的 GA4 測量 ID
    const measurementId = 'G-E5X30486VJ'
    
    result.isConfigured = true
    result.measurementId = measurementId

    // 檢查 gtag 腳本是否載入
    const gtagScript = document.querySelector('script[src*="googletagmanager.com/gtag/js"]')
    result.isScriptLoaded = !!gtagScript
    
    if (!result.isScriptLoaded) {
      result.warnings.push('⚠️ GA4 gtag 腳本未載入')
      result.recommendations.push('請確認 analytics.ts 中的腳本載入邏輯')
    }

    // 檢查 gtag 函數是否可用
    result.isGtagAvailable = typeof (window as any).gtag === 'function'
    
    if (!result.isGtagAvailable && result.isScriptLoaded) {
      result.warnings.push('⚠️ gtag 腳本已載入但函數不可用')
      result.recommendations.push('請檢查腳本載入順序或網路連接')
    }

    // 測試數據發送
    if (result.isGtagAvailable && result.measurementId) {
      try {
        const testResult = await this.testDataSending(result.measurementId)
        result.isDataSending = testResult
        
        if (!testResult) {
          result.warnings.push('⚠️ 測試事件發送失敗')
          result.recommendations.push('請檢查網路連接或 GA4 設定')
        }
      } catch (error) {
        result.errors.push(`❌ 數據發送測試失敗: ${error}`)
      }
    }

    return result
  }

  /**
   * 測試數據是否能正確發送到 GA4
   */
  private async testDataSending(measurementId: string): Promise<boolean> {
    return new Promise((resolve) => {
      try {
        if (typeof (window as any).gtag === 'function') {
          // 發送測試事件
          (window as any).gtag('event', 'ga4_debug_test', {
            event_category: 'Debug',
            event_label: 'Connection Test',
            transport_type: 'beacon',
            custom_parameter_debug: true
          })
          
          // 簡單的超時檢測
          setTimeout(() => resolve(true), 1000)
        } else {
          resolve(false)
        }
      } catch (error) {
        console.warn('GA4 測試發送失敗:', error)
        resolve(false)
      }
    })
  }

  /**
   * 顯示診斷結果
   */
  displayDiagnostic(result: GA4DiagnosticResult): void {
    console.group('🔍 Google Analytics 4 連接診斷')
    
    console.log(`📊 配置狀態: ${result.isConfigured ? '✅ 已配置' : '❌ 未配置'}`)
    console.log(`🆔 測量 ID: ${result.measurementId || '未設定'}`)
    console.log(`📜 腳本載入: ${result.isScriptLoaded ? '✅ 已載入' : '❌ 未載入'}`)
    console.log(`🏷️ gtag 可用: ${result.isGtagAvailable ? '✅ 可用' : '❌ 不可用'}`)
    console.log(`📡 數據發送: ${result.isDataSending ? '✅ 正常' : '❌ 異常'}`)
    
    if (result.errors.length > 0) {
      console.group('❌ 錯誤')
      result.errors.forEach(error => console.error(error))
      console.groupEnd()
    }
    
    if (result.warnings.length > 0) {
      console.group('⚠️ 警告')
      result.warnings.forEach(warning => console.warn(warning))
      console.groupEnd()
    }
    
    if (result.recommendations.length > 0) {
      console.group('💡 建議')
      result.recommendations.forEach(rec => console.info(rec))
      console.groupEnd()
    }
    
    console.groupEnd()
  }

  /**
   * 實時監控 GA4 事件
   */
  startEventMonitoring(): void {
    if (typeof (window as any).gtag !== 'function') {
      console.warn('⚠️ gtag 不可用，無法啟動事件監控')
      return
    }

    // 監控原生 gtag 調用
    const originalGtag = (window as any).gtag
    let eventCount = 0

    ;(window as any).gtag = function(...args: any[]) {
      if (args[0] === 'event') {
        eventCount++
        console.log(`📊 GA4 事件 #${eventCount}:`, {
          event: args[1],
          parameters: args[2],
          timestamp: new Date().toISOString()
        })
      }
      
      return originalGtag.apply(this, args)
    }

    console.log('🔍 GA4 事件監控已啟動')
  }

  /**
   * 獲取環境資訊
   */
  getEnvironmentInfo(): Record<string, any> {
    return {
      hostname: window.location.hostname,
      isLocalhost: ['localhost', '127.0.0.1'].includes(window.location.hostname),
      userAgent: navigator.userAgent,
      language: navigator.language,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      timestamp: new Date().toISOString()
    }
  }
}

/**
 * 控制台快捷指令
 */
export function setupGA4DebugCommands(): void {
  if (typeof window !== 'undefined') {
    const ga4Debugger = GA4Debugger.getInstance()
    
    // 提供全域調試指令
    ;(window as any).GA4Debug = {
      // 診斷連接
      async check() {
        const result = await ga4Debugger.diagnose()
        ga4Debugger.displayDiagnostic(result)
        return result
      },
      
      // 啟動事件監控
      monitor() {
        ga4Debugger.startEventMonitoring()
      },
      
      // 發送測試事件
      testEvent() {
        if (typeof (window as any).gtag === 'function') {
          (window as any).gtag('event', 'debug_test_event', {
            event_category: 'Debug',
            event_label: 'Manual Test',
            value: Math.floor(Math.random() * 100)
          })
          console.log('✅ 測試事件已發送')
        } else {
          console.error('❌ gtag 不可用')
        }
      },
      
      // 環境資訊
      env() {
        const info = ga4Debugger.getEnvironmentInfo()
        console.table(info)
        return info
      }
    }
    
    console.log(`
🔍 GA4 調試指令已載入！使用方式：

GA4Debug.check()     - 診斷連接狀態
GA4Debug.monitor()   - 啟動事件監控  
GA4Debug.testEvent() - 發送測試事件
GA4Debug.env()       - 查看環境資訊
    `)
  }
}

export default GA4Debugger 